import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }} />

        <motion.div
          className="absolute bottom-20 right-10 w-48 h-48 bg-accent/10 rounded-full blur-xl"
          animate={{
            x: [0, -40, 0],
            y: [0, 20, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }} />

      </div>
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8">

            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="flex items-center space-x-3">

                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                  <Icon name="Droplets" size={24} color="white" />
                </div>
                <span className="text-primary font-poppins font-semibold text-lg">Our Story</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-text-primary leading-tight">

                Pure Water,
                <span className="text-transparent bg-gradient-to-r from-primary to-accent bg-clip-text"> Fresh Moments</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="text-lg text-text-secondary font-inter leading-relaxed max-w-2xl">

                We're new to the market, but our commitment to water purity is unwavering. At Mava, we focus on creating the cleanest, most refreshing water bottles using recycled plastic materials, bringing you premium hydration that's both pure and responsible.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex flex-wrap gap-6">

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <Icon name="Droplets" size={20} color="rgb(59 130 246)" />
                </div>
                <span className="font-inter font-medium text-text-primary">Ultra Pure Water</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                  <Icon name="Recycle" size={20} color="rgb(34 197 94)" />
                </div>
                <span className="font-inter font-medium text-text-primary">Recycled Plastic</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Shield" size={20} color="var(--color-primary)" />
                </div>
                <span className="font-inter font-medium text-text-primary">Purity Guaranteed</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Visual Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative">

            <div className="relative w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden liquid-shadow">
              <Image
                src="/assets/images/mava-1762174454410.jpg"
                alt="Mava Cold Beverages premium water bottles showcasing pure, clean design and recycled plastic construction"
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />
              
              {/* Floating Stats */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="absolute top-6 right-6 bg-background/95 backdrop-blur-md rounded-xl p-4 liquid-shadow">

                <div className="text-center">
                  <div className="text-2xl font-poppins font-bold text-primary">99.9%</div>
                  <div className="text-sm text-text-secondary font-inter">Pure Water</div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute bottom-6 left-6 bg-background/95 backdrop-blur-md rounded-xl p-4 liquid-shadow">

                <div className="text-center">
                  <div className="text-2xl font-poppins font-bold text-green-500">100%</div>
                  <div className="text-sm text-text-secondary font-inter">Recycled Plastic</div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <motion.div
              animate={{
                rotate: [0, 360]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-full opacity-20" />

            <motion.div
              animate={{
                rotate: [360, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear"
              }}
              className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full opacity-30" />

          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2">

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center space-y-2">

          <span className="text-sm text-text-secondary font-inter">Scroll to explore</span>
          <Icon name="ChevronDown" size={20} color="var(--color-text-secondary)" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;